// vg-binding: P7.D-29
//
// apps/vendor/src/features/inventory/TransfersTab.tsx
//
// Phase 7 Wave 15 polish — vendor warehouse transfers tab.

import { useState } from 'react';

import { Pagination } from '@printway/ui';

import { useTranslation } from '../../lib/i18n/index.js';
import { useTransfers } from './api.js';
import TransferFormModal from './TransferFormModal.js';

const PAGE_LIMIT = 20;

function shortId(id: string): string {
  return id.length > 8 ? `…${id.slice(-8)}` : id;
}

function fmtDate(ms: number): string {
  return new Date(ms).toLocaleString(undefined, { hour12: false });
}

interface Props {
  onToast: (msg: string) => void;
}

export function TransfersTab({ onToast }: Props): JSX.Element {
  const { t } = useTranslation('inventory');
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);

  const query = useTransfers({ limit: PAGE_LIMIT, page });
  const rows = query.data?.rows ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-end">
        <button
          type="button"
          data-testid="inventory-transfer-create-btn"
          onClick={() => setModalOpen(true)}
          className="rounded-md bg-pw-primary px-4 py-2 text-sm font-semibold text-white hover:bg-pw-primary-strong"
        >
          {t('transfers.createButton')}
        </button>
      </div>

      {query.isLoading && rows.length === 0 ? (
        <p className="px-4 py-6 text-sm text-pw-text-muted">{t('transfers.loading')}</p>
      ) : null}

      {!query.isLoading && rows.length === 0 ? (
        <div
          data-testid="inventory-transfers-empty"
          className="rounded-xl border border-dashed border-pw-border bg-pw-surface px-6 py-10 text-center"
        >
          <p className="text-sm text-pw-text">{t('transfers.emptyTitle')}</p>
          <p className="mt-1 text-xs text-pw-text-muted">
            {t('transfers.emptyHint')}
          </p>
        </div>
      ) : null}

      {rows.length > 0 ? (
        <div className="overflow-hidden rounded-xl border border-pw-border bg-pw-bg">
          <div className="overflow-x-auto">
          <table
            data-testid="inventory-transfers-table"
            className="w-full border-collapse text-sm"
          >
            <thead className="bg-pw-surface text-left text-xs uppercase tracking-wider text-pw-text-muted">
              <tr>
                <th className="px-4 py-2">{t('transfers.headerProduct')}</th>
                <th className="px-4 py-2">{t('transfers.headerVariant')}</th>
                <th className="px-4 py-2">{t('transfers.headerFromTo')}</th>
                <th className="px-4 py-2 text-right">{t('transfers.headerQty')}</th>
                <th className="px-4 py-2">{t('transfers.headerNote')}</th>
                <th className="px-4 py-2">{t('transfers.headerDate')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.transfer_id}
                  data-testid={`inventory-transfer-row-${r.transfer_id}`}
                  className="border-t border-pw-border hover:bg-pw-surface"
                >
                  <td className="px-4 py-2 text-pw-text">
                    <span className="font-medium">
                      {r.product_name ?? shortId(r.variant_id)}
                    </span>
                    {r.variant_sku ? (
                      <span className="ml-1 text-xs text-pw-text-muted">({r.variant_sku})</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 text-pw-text-muted">
                    {r.variant_label ?? shortId(r.variant_id)}
                  </td>
                  <td className="px-4 py-2 text-pw-text-muted">
                    {r.from_warehouse_name ?? shortId(r.from_warehouse_id)}
                    {' → '}
                    {r.to_warehouse_name ?? shortId(r.to_warehouse_id)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-pw-text">{r.quantity}</td>
                  <td className="px-4 py-2 text-pw-text-muted">{r.reason}</td>
                  <td className="px-4 py-2 text-pw-text-muted">{fmtDate(r.occurred_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      ) : null}

      <Pagination
        currentPage={page}
        pageCount={query.data?.page_count ?? 0}
        totalRows={query.data?.total ?? 0}
        testId="inventory-transfers-pagination"
        disabled={query.isFetching}
        onChange={setPage}
      />

      <TransferFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={(msg) => {
          setModalOpen(false);
          // Snap back to page 1 so the newest transfer is visible.
          setPage(1);
          onToast(msg);
        }}
      />
    </div>
  );
}

export default TransfersTab;
